import { useCallback, useEffect, useState } from 'react';

type UseFallbackResourceSourceParams = {
  fallback_source_url: string | null;
  primary_source_url: string;
  should_load_source: boolean;
  timeout_ms: number;
};

function use_fallback_resource_source({
  fallback_source_url,
  primary_source_url,
  should_load_source,
  timeout_ms,
}: UseFallbackResourceSourceParams) {
  const [source_url, set_source_url] = useState(primary_source_url);
  const [has_source_loaded, set_has_source_loaded] = useState(false);
  const can_switch_to_fallback_source =
    Boolean(fallback_source_url) &&
    source_url === primary_source_url &&
    fallback_source_url !== primary_source_url;

  useEffect(() => {
    set_source_url(primary_source_url);
  }, [primary_source_url]);

  useEffect(() => {
    set_has_source_loaded(false);
  }, [source_url]);

  useEffect(() => {
    if (
      !should_load_source ||
      has_source_loaded ||
      !can_switch_to_fallback_source
    ) {
      return;
    }

    // Keep COS as the fast path, but avoid blank media during long stalls.
    const fallback_timeout_id = window.setTimeout(() => {
      if (fallback_source_url) {
        set_source_url(fallback_source_url);
      }
    }, timeout_ms);

    return () => {
      window.clearTimeout(fallback_timeout_id);
    };
  }, [
    can_switch_to_fallback_source,
    fallback_source_url,
    has_source_loaded,
    should_load_source,
    timeout_ms,
  ]);

  const handle_source_error = useCallback(() => {
    if (!can_switch_to_fallback_source || !fallback_source_url) {
      return false;
    }

    set_source_url(fallback_source_url);
    return true;
  }, [can_switch_to_fallback_source, fallback_source_url]);

  const handle_source_load = useCallback(() => {
    set_has_source_loaded(true);
  }, []);

  return {
    handle_source_error,
    handle_source_load,
    source_url,
  };
}

export { use_fallback_resource_source };
